import { fabric } from 'fabric';
import urlHelper from '../../frontend/services/helpers/url';
import { ADDITIONAL_ICONS_PREFIX_ID, ICONS_PREFIX_ID } from "@frontend/constants/templates-field-prefixes";
import {parseJsonData} from "@frontend/services/helpers";
import { deleteURLParameters, setURLParameters } from "@frontend/utils";

export const ICONS_DATA_FIELD = 'icons';
export const ADDITIONAL_ICONS_DATA_FIELD = 'add_icons';

const PLACEMENT_ORDER = {
  ASC: 'asc',
  DEC: 'dec',
}

const PLACEMENT_ORIENTATION = {
  RIGHT_TO_LEFT: 'rightToLeft',
  LEFT_TO_RIGHT: 'leftToRight',
  UP_TO_DOWN: 'upToDown',
  DOWN_TO_UP: 'downToUp',
}

const ICONS_GRID = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
}

const OBJECT_FIT_FILL_QUERY_KEYS = [
  'width_to_fit',
  'height_to_fit',
  'align_to_fit',
  'width_to_fill',
  'height_to_fill',
  'align_to_fill',
  'objectFillCrop',
  'full_resolution',
];

function hasValidAlignment(alignment) {
  return typeof alignment === 'string' && alignment.includes(',');
}

function prepareIconUrlWithObjectFitAndFill(url, options, iconGroupSettings) {
  const normalizedUrl = urlHelper.normalizeImgUrl(url);
  const iconWidth = Number(options?.width) || 0;
  const iconHeight = Number(options?.height) || 0;

  if (!iconWidth || !iconHeight) {
    return {
      url: normalizedUrl,
      hasObjectFitOrFill: false,
    };
  }

  const objectFit = iconGroupSettings?.objectFit;
  const objectFill = iconGroupSettings?.objectFill;
  const hasObjectFit = hasValidAlignment(objectFit);
  const hasObjectFill = hasValidAlignment(objectFill);

  if (!hasObjectFit && !hasObjectFill) {
    return {
      url: normalizedUrl,
      hasObjectFitOrFill: false,
    };
  }

  let preparedUrl = deleteURLParameters(normalizedUrl, OBJECT_FIT_FILL_QUERY_KEYS);

  if (hasObjectFit) {
    preparedUrl = setURLParameters(preparedUrl, {
      width_to_fit: Math.round(iconWidth),
      height_to_fit: Math.round(iconHeight),
      align_to_fit: objectFit,
    });
  }

  if (hasObjectFill) {
    preparedUrl = setURLParameters(preparedUrl, {
      width_to_fill: Math.round(iconWidth),
      height_to_fill: Math.round(iconHeight),
      align_to_fill: objectFill,
    });
  }

  return {
    url: preparedUrl,
    hasObjectFitOrFill: true,
  };
}

function createIconImage(url, options) {


  return new Promise((resolve, reject) => {
    const _url = options.processedUrl || urlHelper.normalizeImgUrl(url);

    fabric.Image.fromURL(_url, (img) => {
      if (!img) {
        return reject(new Error(`Failed to load image: ${url}`));
      }

      const originalWidth = img.width;
      const originalHeight = img.height;

      if (!originalWidth || !originalHeight) {
        return reject(new Error(`Invalid image dimensions for: ${url}`));
      }

      const targetWidth = options.width;
      const targetHeight = options.height;

      const hasObjectFitOrFill = !!options.hasObjectFitOrFill;

      const scaleX = targetWidth / originalWidth;
      const scaleY = hasObjectFitOrFill
        ? targetHeight / originalHeight
        : scaleX;

      img.set({
        id: options.id,
        element_id: options.element_id,
        name: options.name,
        top: options.top,
        left: options.left,
        scaleX,
        scaleY,
        opacity: options.opacity,
        order: options.order,
      });
      
      resolve(img);
    });
  });
}

async function prepareIconsGroups(icons, prefix, canvas, data, fields) {
  const iconGroups = [];

  for (let iconsField = 0; iconsField < icons.length; iconsField++) {
    const {
      id,
      elements,
      touched,
      placementOrder,
      placementOrientation,
      field_name,
      field_element_id,
      iconsGrid,
      iconsInRow,
      iconsRows,
      horizontalOffset,
      verticalOffset,
      objectFit,
      objectFill,
    } = icons[iconsField];
    
    if (!elements?.length) {
      continue;
    }
    
    const _elements = [...elements];
    let iconImages = [];
    
    if (placementOrder === PLACEMENT_ORDER.DEC) {
      _elements.reverse();
    }

    let left;
    let top;
    let spacing = 10;

    let relatedField = null;
    let relatedFieldOptions = data[field_element_id + '_related_field'];

    // POR-9357
    // Temporarily disabled related field logic because of BE rendering
    // if (relatedFieldOptions) {
    //   relatedField = await getRelatedField(icons, canvas, relatedFieldOptions, data, fields)
    // }

    let relatedY = null;
    if (relatedField) {
      relatedY = relatedField.bottom;
    }

    const isGridEnabled = iconsGrid === ICONS_GRID.ENABLED;
    const gridIconsInRow = Math.max(Number(iconsInRow) || 2, 1);
    const gridRows = Math.max(Number(iconsRows) || 2, 1);
    const gridHorizontalOffset = Number.isFinite(Number(horizontalOffset)) ? Number(horizontalOffset) : 20;
    const gridVerticalOffset = Number.isFinite(Number(verticalOffset)) ? Number(verticalOffset) : 20;
    const maxGridItems = gridIconsInRow * gridRows;
    const actualIconCount = isGridEnabled ? Math.min(_elements.length, maxGridItems) : 0;
    const actualGridRows = isGridEnabled ? Math.ceil(actualIconCount / gridIconsInRow) : 0;

    for (let i = 0; i < _elements.length; i++) {
      const options = _elements[i];

      if (isGridEnabled && i >= maxGridItems) {
        break;
      }

      // For grid-enabled layout, only use the first icon's position as base
      // For non-grid layout, use each icon's position when touched or first icon when not touched
      if (isGridEnabled) {
        if (i === 0) {
          left = options.left;
          top = relatedY && relatedY > options.top ? relatedY : options.top;
        }
      } else {
        if (touched || !touched && i === 0) {
          left = options.left;
          top = relatedY && relatedY > options.top ? relatedY : options.top;
        } else if (relatedY) {
          top = relatedY;
        }
      }

      let currentLeft = Number(left);
      let currentTop = Number(top);

      if (isGridEnabled) {
        const row = Math.floor(i / gridIconsInRow);
        const col = i % gridIconsInRow;

        switch (placementOrientation) {
          case PLACEMENT_ORIENTATION.RIGHT_TO_LEFT:
            // Mirror columns: col 0 → rightmost, last col → leftmost (same bounding box)
            currentLeft = Number(left) + (gridIconsInRow - 1 - col) * (Number(options.width) + gridHorizontalOffset);
            currentTop = Number(top) + row * (Number(options.height) + gridVerticalOffset);
            break;

          case PLACEMENT_ORIENTATION.DOWN_TO_UP:
            // Mirror rows: row 0 → bottommost, last row → topmost (same bounding box)
            currentLeft = Number(left) + col * (Number(options.width) + gridHorizontalOffset);
            currentTop = Number(top) + (actualGridRows - 1 - row) * (Number(options.height) + gridVerticalOffset);
            break;

          case PLACEMENT_ORIENTATION.UP_TO_DOWN:
          case PLACEMENT_ORIENTATION.LEFT_TO_RIGHT:
          default:
            currentLeft = Number(left) + col * (Number(options.width) + gridHorizontalOffset);
            currentTop = Number(top) + row * (Number(options.height) + gridVerticalOffset);
            break;
        }
      }

      const iconUrlSettings = prepareIconUrlWithObjectFitAndFill(options.url, options, {
        objectFit,
        objectFill,
      });

      try {
        const iconImage = await createIconImage(options.url, {
          processedUrl: iconUrlSettings.url,
          hasObjectFitOrFill: iconUrlSettings.hasObjectFitOrFill,
          id: `${prefix}${id}_${i}`,
          element_id: `${prefix}${id}_${i}`,
          name: `${prefix}${id}_${i}`,
          top: currentTop + Number(options.top_offset || 0),
          left: currentLeft,
          width: Number(options.width),
          height: Number(options.height),
          opacity: Number(options.opacity) || 1,
          order: Number(options.order)
        });

        iconImages.push(iconImage);
      } catch (error) {
        // One broken icon must not take down the rest of the icons on the canvas
        console.warn('[drawIcons] Skipping icon that failed to load: ', error);
      }
      
      if (!isGridEnabled) {
        // Since width may be different for different icons, we need to calculate spacing based on the next icon element for RIGHT_TO_LEFT only
        const nextWidth = _elements[i+1]?.width ?? 0;

        switch (placementOrientation) {
          case PLACEMENT_ORIENTATION.LEFT_TO_RIGHT:
            left += options.width + spacing;
            break;

          case PLACEMENT_ORIENTATION.RIGHT_TO_LEFT:
            // As we move from right to left, we need to take the width of the next icon to calculate the correct position for the next image
            left -= nextWidth + spacing;
            break;

          case PLACEMENT_ORIENTATION.UP_TO_DOWN:
            top += options.height + spacing;
            break;

          case PLACEMENT_ORIENTATION.DOWN_TO_UP:
            top -= options.height + spacing;
            break;

          default:
            left += options.width + spacing;
        }
      }
    }

    if (iconImages.length) {
      const group = new fabric.Group(iconImages, { 
        id: `${prefix}${id}`,
      });
      
      iconGroups.push(group);
    }

    iconImages = [];
  }

  return iconGroups;
}

export async function drawIcons({ canvas, data, fields}) {
  try {
    canvas.getObjects().forEach(function (o) {
      if (o.id.startsWith(ICONS_PREFIX_ID)) {
        canvas.remove(o);
      }
    });

    if (!data.icons?.length) return;

    const iconGroups = await prepareIconsGroups(data.icons, ICONS_PREFIX_ID, canvas, data, fields);
    
    iconGroups.forEach(group => canvas.add(group));
  } catch (error) {
    console.log('An error occurred in the drawIcons method: ', error);
  }
  
}

export async function drawAdditionalIcons({canvas, data, fields}) {
  canvas.getObjects().forEach(function (o) {
    if (o.id.startsWith(ADDITIONAL_ICONS_PREFIX_ID)) {
      canvas.remove(o);
    }
  });

  if (!data[ADDITIONAL_ICONS_DATA_FIELD]?.length) return;

  const iconGroups = await prepareIconsGroups(data[ADDITIONAL_ICONS_DATA_FIELD], ADDITIONAL_ICONS_PREFIX_ID, canvas, data, fields);

  iconGroups.forEach(group => canvas.add(group));
}

/**
 *
 *
 * @return {?float}
 */
const getRelatedField = async (
  icons,
  canvas,
  options,
  data,
  fields,
  retries=0,
) => {
  var relatedFieldName = "";
  var margin = 5;
  var relatedFieldType = null;

  try {
    let parsedOption = parseJsonData(options, null);

    if (parsedOption && parsedOption.field && parsedOption.field.trim() !== "") {
      relatedFieldName = parsedOption.field;
    }
    if (parsedOption && parsedOption.margin !== undefined) {
      margin = parsedOption.margin;
    }
  } catch (e) {
    console.error("Invalid JSON string", e);
  }

  if (!relatedFieldName) {
    return null;
  }

  let field = fields.filter(f => f.name === relatedFieldName)[0] || null;
  if (!field) {
    return null;
  }

  var canvasObjects = canvas.getObjects();
  var relatedField = null;
  canvasObjects.forEach((obj) => {
    if (obj.templateField && obj.templateField.name === relatedFieldName) {
      relatedField = obj;
    } else if (obj.name && obj.name === relatedFieldName) {
      relatedField = obj;
    }
  });

  if (relatedField) {
    return {
      left: relatedField.left,
      top: relatedField.top,
      width: relatedField.width,
      height: relatedField.height,
      margin: margin,
      bottom: relatedField.top + relatedField.height + margin,
    };
  } else {
    // Retry logic
    await new Promise((r) => setTimeout(r, 1200));
    if (retries < 20) {
      return await getRelatedField(icons, canvas, options, data, fields, retries + 1);
    } else {
      return null;
    }
  }
}
